
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { Card, Table, Button, Select, Space, Tag, Tooltip, message, Modal, Dropdown, Avatar } from 'antd';
import { SyncOutlined, DeleteOutlined, ReloadOutlined, MoreOutlined, ExclamationCircleOutlined, AppleOutlined, AndroidOutlined, MobileOutlined } from '@ant-design/icons';
import moment from 'moment';
import { RootState } from '../../app/store';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/';

interface User {
    _id: string;
    email: string;
    displayName?: string;
    name?: string;
    avatar?: string;
}

interface Subscription {
    id: string;
    userId: User;
    productId: string;
    transactionId: string;
    originalTransactionId: string;
    purchaseDate: string;
    expiresDate: string;
    isActive: boolean;
    platform: string;
    createdAt: string;
    updatedAt: string;
}

interface PaginationInfo {
    total: number;
    page: number;
    totalPages: number;
    limit: number;
}

interface SubscriptionResponse {
    status: boolean;
    message: string;
    data: {
        subscriptions: Subscription[];
        pagination: PaginationInfo;
    };
}

const SubscriptionPage = () => {
    const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
    const [pagination, setPagination] = useState<PaginationInfo>({
        total: 0,
        page: 1,
        totalPages: 0,
        limit: 20
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [currentPage, setCurrentPage] = useState(1);
    // Get auth token from Redux store
    const authToken = useSelector((state: RootState) => state.auth.token);

    const fetchSubscriptions = useCallback(async (page: number = 1, status: string = '') => {
        setLoading(true);
        setError(null);
        
        try {
            const params = new URLSearchParams({
                page: page.toString(),
                limit: pagination.limit.toString()
            });
            
            if (status) {
                params.append('status', status);
            }

            const response = await axios.get<SubscriptionResponse>(
                `${API_BASE_URL}subscription/admin/all?${params.toString()}`,
                {
                    headers: {
                        'Authorization': `Bearer ${authToken || ''}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.status) {
                setSubscriptions(response.data.data.subscriptions);
                setPagination(response.data.data.pagination);
            } else {
                setError('Failed to fetch subscriptions');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Error fetching subscriptions');
            console.error('Error fetching subscriptions:', err);
        } finally {
            setLoading(false);
        }
    }, [authToken, pagination.limit]);

    const deleteSubscription = async (subscriptionId: string) => {
        Modal.confirm({
            title: 'Xác nhận xóa',
            icon: <ExclamationCircleOutlined />,
            content: 'Bạn có chắc chắn muốn xóa subscription này không?',
            okText: 'Xóa',
            okType: 'danger',
            cancelText: 'Hủy',
            onOk: async () => {
                try {
                    const response = await axios.delete(
                        `${API_BASE_URL}subscription/admin/${subscriptionId}`,
                        {
                            headers: {
                                'Authorization': `Bearer ${authToken || ''}`,
                                'Content-Type': 'application/json'
                            }
                        }
                    );

                    if (response.data.status) {
                        // Refresh the list after successful deletion
                        await fetchSubscriptions(currentPage, statusFilter);
                        message.success('Xóa subscription thành công');
                    } else {
                        message.error('Xóa subscription thất bại');
                    }
                } catch (err: any) {
                    message.error(err.response?.data?.message || 'Lỗi khi xóa subscription');
                    console.error('Error deleting subscription:', err);
                } finally {
                }
            }
        });
    };

    const resetPremium = async (userId: string) => {
        Modal.confirm({
            title: 'Xác nhận reset Premium',
            icon: <ExclamationCircleOutlined />,
            content: 'Thao tác này sẽ tắt toàn bộ gói Premium đang active/pending của user và giữ lại lịch sử subscription. Bạn có chắc chắn muốn tiếp tục không?',
            okText: 'Reset Premium',
            okType: 'danger',
            cancelText: 'Hủy',
            onOk: async () => {
                try {
                    const response = await axios.post(
                        `${API_BASE_URL}subscription/admin/user/${userId}/reset-premium`,
                        {},
                        {
                            headers: {
                                'Authorization': `Bearer ${authToken || ''}`,
                                'Content-Type': 'application/json'
                            }
                        }
                    );

                    if (response.data.status) {
                        await fetchSubscriptions(currentPage, statusFilter);
                        message.success(
                            `Đã reset Premium (${response.data.data?.revokedSubscriptions ?? 0} gói)`
                        );
                    } else {
                        message.error(response.data.message || 'Reset Premium thất bại');
                    }
                } catch (err: any) {
                    message.error(err.response?.data?.message || 'Lỗi khi reset Premium');
                    console.error('Error resetting Premium:', err);
                } finally {
                }
            }
        });
    };

    const handleStatusFilterChange = (status: string) => {
        setStatusFilter(status);
        setCurrentPage(1);
        fetchSubscriptions(1, status);
    };

    const formatDateWithTooltip = (dateString: string) => {
        const localTime = moment.utc(dateString).local();
        const utcTime = moment.utc(dateString);
        return (
            <Tooltip title={`UTC: ${utcTime.format('DD/MM/YYYY HH:mm')}`}>
                <span>{localTime.format('DD/MM/YYYY HH:mm')}</span>
            </Tooltip>
        );
    };

    const getStatusTag = (subscription: Subscription) => {
        const isExpired = moment.utc(subscription.expiresDate).isBefore(moment());
        const isActive = subscription.isActive && !isExpired;
        
        if (isActive) {
            return <Tag color="success">Active</Tag>;
        } else if (isExpired) {
            return <Tag color="error">Expired</Tag>;
        } else {
            return <Tag color="default">Inactive</Tag>;
        }
    };

    useEffect(() => {
        fetchSubscriptions();
    }, [fetchSubscriptions]);

    const columns = [
        {
            title: '#',
            dataIndex: 'index',
            key: 'index',
            width: 60,
            render: (_: any, __: any, index: number) => 
                (currentPage - 1) * pagination.limit + index + 1
        },
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 100,
            render: (id: string) => (
                <Tooltip title={id}>
                    <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                        {id.slice(-8)}
                    </span>
                </Tooltip>
            )
        },
        {
            title: 'Người dùng',
            dataIndex: 'userId',
            key: 'userId',
            width: 200,
            render: (user: User) => {
                const displayName = user.displayName || user.name || user.email || 'N/A';
                const avatarText = displayName.charAt(0).toUpperCase();

                return (
                    <Space size="small" align="center">
                        <Avatar src={user.avatar || undefined} size="small">
                            {avatarText}
                        </Avatar>
                        <div>
                            <div style={{ fontWeight: 'bold' }}>{displayName}</div>
                            <div style={{ fontSize: '12px', color: '#666' }}>{user.email}</div>
                        </div>
                    </Space>
                );
            }
        },
        {
            title: 'Sản phẩm',
            dataIndex: 'productId',
            key: 'productId',
            width: 100,
            render: (productId: string) => (
                <Tag color="blue">
                    {productId.replace('com.briant.monea.', '')}
                </Tag>
            )
        },
        {
            title: 'Nền tảng',
            dataIndex: 'platform',
            key: 'platform',
            width: 70,
            align: 'center' as const,
            render: (platform: string) => {
                const normalizedPlatform = platform?.toLowerCase();
                const platformIcon = normalizedPlatform === 'ios'
                    ? <AppleOutlined />
                    : normalizedPlatform === 'android'
                        ? <AndroidOutlined />
                        : <MobileOutlined />;

                return (
                    <Tooltip title={platform?.toUpperCase() || 'UNKNOWN'}>
                        <span style={{ fontSize: '20px', lineHeight: 1 }}>
                            {platformIcon}
                        </span>
                    </Tooltip>
                );
            }
        },
        {
            title: 'Ngày mua',
            dataIndex: 'purchaseDate',
            key: 'purchaseDate',
            width: 150,
            render: (date: string) => formatDateWithTooltip(date),
            sorter: (a: Subscription, b: Subscription) => 
                moment.utc(a.purchaseDate).valueOf() - moment.utc(b.purchaseDate).valueOf()
        },
        {
            title: 'Ngày hết hạn',
            dataIndex: 'expiresDate',
            key: 'expiresDate',
            width: 150,
            render: (date: string) => formatDateWithTooltip(date),
            sorter: (a: Subscription, b: Subscription) => 
                moment.utc(a.expiresDate).valueOf() - moment.utc(b.expiresDate).valueOf()
        },
        {
            title: 'Trạng thái',
            key: 'status',
            width: 85,
            render: (_: any, record: Subscription) => getStatusTag(record)
        },
        {
            title: 'Thao tác',
            key: 'actions',
            width: 70,
            align: 'center' as const,
            render: (_: any, record: Subscription) => {
                const userId = record.userId?._id;
                const menuItems = [
                    {
                        key: 'delete',
                        label: 'Xóa subscription',
                        icon: <DeleteOutlined />,
                        danger: true,
                    },
                    ...(userId ? [{
                        key: 'reset',
                        label: 'Reset Pro',
                        icon: <ReloadOutlined />,
                        danger: true,
                    }] : []),
                ];

                return (
                    <Dropdown
                        trigger={['click']}
                        menu={{
                            items: menuItems,
                            onClick: ({ key }) => {
                                if (key === 'delete') {
                                    deleteSubscription(record.id);
                                } else if (key === 'reset' && userId) {
                                    resetPremium(userId);
                                }
                            },
                        }}
                    >
                        <Button
                            type="text"
                            icon={<MoreOutlined />}
                            aria-label={`Thao tác subscription ${record.id}`}
                        />
                    </Dropdown>
                );
            }
        }
    ];

    const paginationConfig = {
        current: currentPage,
        pageSize: pagination.limit,
        total: pagination.total,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total: number, range: [number, number]) => 
            `${range[0]}-${range[1]} của ${total} mục`,
        onChange: (page: number, pageSize: number) => {
            setCurrentPage(page);
            setPagination(prev => ({ ...prev, limit: pageSize }));
            fetchSubscriptions(page, statusFilter);
        }
    };

    return (
        <Card title="Quản lý Subscription" className="subscription-page">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {/* Filters */}
                <Space>
                    <span>Lọc theo trạng thái:</span>
                    <Select
                        style={{ width: 150 }}
                        value={statusFilter}
                        onChange={handleStatusFilterChange}
                        options={[
                            { value: '', label: 'Tất cả' },
                            { value: 'active', label: 'Hoạt động' },
                            { value: 'expired', label: 'Hết hạn' },
                            { value: 'inactive', label: 'Không hoạt động' }
                        ]}
                    />
                    <Button 
                        type="primary"
                        icon={<SyncOutlined />}
                        loading={loading}
                        onClick={() => fetchSubscriptions(currentPage, statusFilter)}
                    >
                        Làm mới
                    </Button>
                </Space>

                {/* Error Message */}
                {error && (
                    <div style={{ color: '#ff4d4f', padding: '8px 12px', backgroundColor: '#fff2f0', border: '1px solid #ffccc7', borderRadius: '6px' }}>
                        {error}
                    </div>
                )}

                {/* Subscriptions Table */}
                <Table
                    columns={columns}
                    dataSource={subscriptions}
                    rowKey="id"
                    loading={loading}
                    pagination={paginationConfig}
                    size="middle"
                    scroll={{ x: 'max-content' }}
                    locale={{
                        emptyText: 'Không có subscription nào'
                    }}
                />
            </Space>
        </Card>
    );
};

export default SubscriptionPage;
